import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from "yup";
import toastr from "toastr";
import _ from 'lodash';
import {
    createAdminFarm,
    getAdminFarm, getAllCountries, getAllReports, getOrganisationData,
    resetResponseState, setAdminFarmId, setReportId, setLoading, unsetLoading, updateAdminFarm,
    getMyAccount
} from '../../../store/appAction';
import { UserRole } from '../../../helpers/userHelper';
import { toastrCustomOptions, toastrCustomOptionsLonger, toastrCustomOptionsNoFarmSelectPopUp } from '../../../constants/toastrOptions';
import { ThemeProvider } from '@emotion/react';
import { FormHeaderPaper, FormTitleTypography, MyFarmPaper } from '../../../components/CustomStyles/StyledComponents';
import { mainTheme } from '../../../theme';
import { Grid } from '@mui/material';
import FarmForm from '../../../components/Forms/FarmForm';
import { useParams, useLocation } from 'react-router-dom';
import { ukPostCodeAlt } from '../../../constants/regex';
import { CONSULTANT,ADMIN,SUPER_ADMIN } from '../../../constants/userRoles';
import { EDIT,CREATE} from '../../../constants/modes';
import { addYears, getNowDate } from '../../../helpers/dateHelper'


const formEmpty = {
    id: '',
    farmName: '',
    farmOwnerUserId: '',
    holdingNumber: '',
    forganisation: '',
    organisationId: '',
    foffice: '',
    officeId: '',
    addressLine1: '',
    addressLine2: '',
    townCity: '',
    countryId: '',
    zipPostCode: '',
    businessReferenceNumber: '',
    farmAdvisoryService: false,
    license: 1,
    isUpgraded: 0,
    initialFarmOwner: '',
    externalFarmId: ''
}


const Farm = () => {
    toastr.options = toastrCustomOptions;
    const dispatch = useDispatch();
    let { mode } = useParams();
    const {pathname} = useLocation();

    if (pathname==='/my-farm'){
        mode=EDIT
    }
    
    const user = useSelector(state => state.auth.user);
    const userDetails = useSelector(state => state.profile.account);
    const farmDetails = useSelector(state => state.adminFarm.farmDetails);
    const farmId = useSelector(state => state.adminFarm.farmId);
    const { countries, successMsg, errorMsg } = useSelector(state => state.common);

    const [formData, setFormData] = useState(formEmpty);
    
    const trimMessage = (fieldName) => {
        return `${fieldName} has leading or trailing spaces`
    }

    const validationSchema = Yup.object({
        farmName: Yup.string().trim(trimMessage("Name")).required("Farm name is required"),
        officeId: Yup.string().trim(trimMessage("Office ID") ),
        organisationId: Yup.string().trim(trimMessage("Organisation ID")),
        farmOwnerUserId: Yup.string().trim(trimMessage("Farm Owner ID")).required("Farm Owner is required"),
        holdingNumber: Yup.string().trim(trimMessage("Holding Number")).required("Holding Number is required"),
        addressLine1: Yup.string().trim(trimMessage("Address 1")),
        addressLine2: Yup.string().trim(trimMessage("Address 2")),
        countryId: Yup.string().trim(trimMessage("Country ID")).required("Country is required"),
        townCity: Yup.string().trim(trimMessage("Town/City")).required("Town/City is required"),
        zipPostCode: Yup.string().trim(trimMessage("Postcode")).required("Postcode is required")
            .when('countryId', {
                is: (countryId) => (_.find(countries, { id: countryId })?.countryCategory || -1) === 1,
                then: () => Yup.string().matches(ukPostCodeAlt, 'Enter a valid postcode'),
            }),
        businessReferenceNumber: Yup.string().trim(trimMessage("Business Reference Number")).required("Business Reference Number is required")
    });

    useEffect(() => {
        dispatch(setLoading());
        if (mode === EDIT && farmId != null) {
            dispatch(getAdminFarm(farmId));
        }
        else if(!UserRole.isFarmer(user.role)) { 
            // Mode is CREATE
            dispatch(getMyAccount());
            dispatch(setReportId(null));
            dispatch(setAdminFarmId(null));
            setFormData({
                id: '',
                farmName: '',
                farmOwnerUserId: user.accountId,
                holdingNumber: '',
                forganisation: farmDetails?.organisation || '',
                organisationId: user.role === CONSULTANT ? farmDetails?.organisationId : '',
                foffice:farmDetails?.office || '',
                officeId: '',
                addressLine1: '',
                addressLine2: '',
                townCity: '',
                countryId: '',
                zipPostCode: '',
                businessReferenceNumber: '',
                farmAdvisoryService: false,
                license: 1,
                initialFarmOwner:userDetails?.fullName,
                externalFarmId:'',
                licenseStartDate: getNowDate(),
                licenseEndDate: addYears(getNowDate(),1)
            });
        }
        dispatch(getAllCountries());
        if(!UserRole.isFarmer(user.role)){
            dispatch(getOrganisationData());
        }
        dispatch(getAllReports({ farmId }));
        return () => {
            setFormData(formEmpty);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (mode === CREATE){
            let consultantInOrg = (user.role === CONSULTANT && userDetails?.organisationId);
            let consultantNoOrg = (user.role === CONSULTANT && !(userDetails?.organisationId))
            let AdminsOrConsultant = [CONSULTANT, ADMIN, SUPER_ADMIN].includes(user.role);
            setFormData({
                id: '',
                farmName: '',
                farmOwnerUserId:  AdminsOrConsultant && !consultantNoOrg ? '' : user.accountId,
                holdingNumber: '',
                forganisation: consultantInOrg ? userDetails?.organisation : '',
                organisationId: consultantInOrg ? userDetails?.organisationId : '',
                foffice: consultantInOrg ? userDetails?.office : '',
                officeId: '',
                addressLine1: '',
                addressLine2: '',
                townCity: '',
                countryId: '',
                zipPostCode: '',
                businessReferenceNumber: '',
                farmAdvisoryService: false,
                license: 1,
                initialFarmOwner: AdminsOrConsultant && !consultantNoOrg? '':userDetails?.fullName,
                externalFarmId:'',
                licenseStartDate: getNowDate(),
                licenseEndDate: addYears(getNowDate(),1)

            });
        }

    }, [userDetails]);

    useEffect(() => {
        if (successMsg || errorMsg) {
            dispatch(unsetLoading());
            successMsg && toastr.success(successMsg);
            errorMsg && toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
            dispatch(resetResponseState());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMsg, errorMsg]);

    useEffect(() => {
        if (farmDetails && !_.isEmpty(farmDetails)) {
            if(!farmDetails?.isUpgraded){
                toastr.warning("This farm cannot be updated as it belongs to a user that has not been approved for Agrecalc cloud.", undefined, toastrCustomOptionsNoFarmSelectPopUp);
            }
            setFormData({
                id: farmDetails?.id || '',
                farmName: farmDetails?.farmName || '',
                farmOwnerUserId: farmDetails?.farmOwnerUserId || '',
                holdingNumber: farmDetails?.holdingNumber || '',
                forganisation: farmDetails?.organisation || '',
                organisationId: farmDetails?.organizationId || '',
                foffice:farmDetails?.office || '',
                officeId: farmDetails?.officeId || '',
                addressLine1: farmDetails?.addressLine1 || '',
                addressLine2: farmDetails?.addressLine2 || '',
                townCity: farmDetails?.townCity || '',
                countryId: farmDetails?.countryId || '',
                zipPostCode: farmDetails?.zipPostCode || '',
                businessReferenceNumber: farmDetails?.businessReferenceNumber || '',
                farmAdvisoryService: farmDetails?.farmAdvisoryService || false,
                license: farmDetails?.license,
                isUpgraded: farmDetails?.isUpgraded,
                initialFarmOwner: farmDetails?.farmOwner || '',
                externalFarmId: farmDetails?.externalFarmId ||'',
                licenseStartDate: farmDetails?.licenseStartDate ,
                licenseEndDate: farmDetails?.licenseEndDate
            });
            dispatch(unsetLoading());
        }
    }, [farmDetails])


    const handleSubmit = (data) => {
        dispatch(setLoading());

        if (mode === EDIT && farmId != null) {
            dispatch(updateAdminFarm(data));
        } else {
            dispatch(createAdminFarm(data));
        }
    }

    let isUsersFarm= farmDetails?.farmOwnerUserId===user?.accountId
    return (
        <ThemeProvider theme={mainTheme}>
            <MyFarmPaper>
                <Grid container spacing={2}>
                    <Grid item xs={8}>
                        <FormHeaderPaper sx={{ marginTop: 1.5, padding: 2 }}>
                            <FormTitleTypography variant="h5" gutterBottom component="div">
                                Farm Account Details
                            </FormTitleTypography>
                        </FormHeaderPaper>
                    </Grid>
                    <Grid textAlign={'right'} item xs={4}>
                        {(mode === EDIT && farmId != null) &&
                            <FormTitleTypography sx={{ margin: 1.5, padding: 2 }} variant="h5" gutterBottom component="div">
                                Farm ID : {farmDetails?.friendlyFarmId}
                            </FormTitleTypography>}
                    </Grid>
                </Grid>
                {formData &&
                    <Formik enableReinitialize initialValues={formData} 
                            validationSchema={validationSchema} onSubmit={handleSubmit}>
                        {props => <FarmForm {...props} role={user?.role} mode={mode} isMyFarm={isUsersFarm} />}
                    </Formik>
                }
            </MyFarmPaper>
        </ThemeProvider>
    )
}

export default Farm